/**
 * OTAVehicleAssignModal
 *
 * Shown when an OTA-provisioned vehicle has no vehicle type or fleet assigned.
 * Allows the operator to assign both so the vehicle can receive OTA deployments.
 */

import React, { memo, useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import API from "Utils/api";

interface VehicleType {
  id: string;
  name: string;
}

interface Fleet {
  id: string;
  name: string;
}

interface OTAVehicleAssignModalProps {
  isOpen: boolean;
  vehicleId: string;
  vehicleThingName?: string;
  vehicleVin: string;
  // When set, this vehicle is linked to Assets → Vehicles, which is now the
  // source of truth for fleet assignment (auto-synced on save). Fleet stays
  // locked here — but vehicle type/VIN are OTA-only concepts Assets doesn't
  // manage, so the rest of the form must stay usable (DEV-4799 follow-up).
  fleetManagedByAssets?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const OTAVehicleAssignModal = memo(
  ({
    isOpen,
    vehicleId,
    vehicleThingName,
    vehicleVin,
    fleetManagedByAssets = false,
    onClose,
    onSuccess,
  }: OTAVehicleAssignModalProps) => {
    const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
    const [fleets, setFleets] = useState<Fleet[]>([]);
    const [selectedTypeId, setSelectedTypeId] = useState<string>("");
    const [selectedFleetId, setSelectedFleetId] = useState<string>("");
    const [vinInput, setVinInput] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(true);

    useEffect(() => {
      if (!isOpen) return;
      console.log(
        "[OTAVehicleAssignModal] opened — vehicleId:",
        vehicleId,
        "vehicleThingName:",
        vehicleThingName
      );
      setSelectedTypeId("");
      setSelectedFleetId("");
      setVinInput("");
      setLoadingOptions(true);
      Promise.all([
        API.get("/ota/ota-vehicle-types/"),
        API.get("/ota/fleets/?is_active=true"),
      ])
        .then(([typesRes, fleetsRes]) => {
          const typesData = Array.isArray(typesRes.data)
            ? typesRes.data
            : typesRes.data?.results || [];
          const fleetsData = Array.isArray(fleetsRes.data)
            ? fleetsRes.data
            : fleetsRes.data?.results || [];
          setVehicleTypes(typesData);
          setFleets(fleetsData);
        })
        .catch(() => toast.error("Failed to load vehicle types and fleets"))
        .finally(() => setLoadingOptions(false));
    }, [isOpen]);

    if (!isOpen) return null;

    const isFormFilled =
      !!selectedTypeId && (fleetManagedByAssets || !!selectedFleetId);

    const handleAssign = async () => {
      if (!isFormFilled) return;
      console.log(
        "[OTAVehicleAssignModal] handleAssign — vehicleId:",
        vehicleId,
        "vehicleThingName:",
        vehicleThingName
      );
      setLoading(true);
      try {
        const payload: Record<string, string> = {
          vehicle_type_id: selectedTypeId,
        };
        if (!fleetManagedByAssets) {
          payload.ota_fleet_id = selectedFleetId;
        }
        if (!vehicleVin && vinInput.trim()) {
          payload.vin = vinInput.trim();
        }
        await API.patch(`/ota/vehicles/${vehicleId}/`, payload);
        console.log(
          "[OTAVehicleAssignModal] PATCH success — toast will show vehicleThingName:",
          vehicleThingName
        );
        toast.success(`Vehicle ${vehicleThingName} assigned successfully`);
        onSuccess();
        onClose();
      } catch (err: unknown) {
        const e = err as { response?: { data?: { detail?: string } } };
        toast.error(e.response?.data?.detail || "Failed to assign vehicle");
      } finally {
        setLoading(false);
      }
    };

    return (
      <Overlay onClick={onClose}>
        <Modal onClick={(e) => e.stopPropagation()}>
          <Header>Assign Vehicle</Header>
          <Body>
            <Field>
              <Label>Thing Name</Label>
              <Value>{vehicleThingName || vehicleId}</Value>
            </Field>
            {vehicleVin && (
              <Field>
                <Label>VIN</Label>
                <Value>{vehicleVin}</Value>
              </Field>
            )}

            {!vehicleVin && (
              <Field>
                <Label>VIN (optional)</Label>
                <TextInput
                  type="text"
                  placeholder="e.g. 1HGCM82633A123456"
                  value={vinInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVinInput(e.target.value.toUpperCase())
                  }
                  maxLength={17}
                />
              </Field>
            )}

            {loadingOptions ? (
              <LoadingText>Loading options…</LoadingText>
            ) : (
              <>
                <Field>
                  <Label>Vehicle Type *</Label>
                  <Select
                    value={selectedTypeId}
                    onChange={(e) => setSelectedTypeId(e.target.value)}
                  >
                    <option value="">— select type —</option>
                    {vehicleTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field>
                  <Label>Fleet {fleetManagedByAssets ? "" : "*"}</Label>
                  {fleetManagedByAssets ? (
                    <ManagedNote>
                      Managed via Assets → Vehicles for this vehicle
                    </ManagedNote>
                  ) : (
                    <Select
                      value={selectedFleetId}
                      onChange={(e) => setSelectedFleetId(e.target.value)}
                    >
                      <option value="">— select fleet —</option>
                      {fleets.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </>
            )}
          </Body>
          <Footer>
            <CancelButton onClick={onClose} disabled={loading}>
              Cancel
            </CancelButton>
            <AssignButton
              onClick={handleAssign}
              disabled={!isFormFilled || loading || loadingOptions}
            >
              {loading ? "Assigning…" : "Assign"}
            </AssignButton>
          </Footer>
        </Modal>
      </Overlay>
    );
  }
);

OTAVehicleAssignModal.displayName = "OTAVehicleAssignModal";

// Styled components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 420px;
  max-width: 95vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 20px 24px 16px;
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  border-bottom: 1px solid #e2e8f0;
`;

const Body = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Value = styled.span`
  font-size: 14px;
  color: #2d3748;
  font-family: monospace;
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  background: #fff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.3);
  }
`;

const ManagedNote = styled.div`
  padding: 8px 10px;
  border: 1px dashed #cbd5e0;
  border-radius: 6px;
  font-size: 13px;
  color: #718096;
  font-style: italic;
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: #718096;
  text-align: center;
  margin: 8px 0;
`;

const Footer = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e2e8f0;
`;

const CancelButton = styled.button`
  padding: 8px 20px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
  color: #4a5568;

  &:hover:not(:disabled) {
    background: #f7fafc;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AssignButton = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: #4299e1;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #3182ce;
  }

  &:disabled {
    background: #a0aec0;
    cursor: not-allowed;
  }
`;

const TextInput = styled.input`
  padding: 8px 10px;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  font-size: 14px;
  color: #2d3748;
  background: #fff;
  font-family: monospace;
  letter-spacing: 0.05em;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.3);
  }
`;
